import React, { useState } from 'react';
import * as XLSX from 'xlsx';

// Define types for our component
interface SqlToExcelProps {}

// Define database types
type ColumnDefinition = {
  name: string;
  type: string;
};

type DatabaseTable = {
  name: string;
  columns: ColumnDefinition[];
  rows: any[][];
};

// Mock database for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
  users: {
    name: 'users',
    columns: [
      { name: 'id', type: 'number' },
      { name: 'name', type: 'string' },
      { name: 'email', type: 'string' },
      { name: 'created_at', type: 'date' }
    ],
    rows: [
      [1, 'John Doe', 'john@example.com', '2023-01-15'],
      [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
      [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
      [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
      [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
    ]
  },
  products: {
    name: 'products',
    columns: [
      { name: 'id', type: 'number' },
      { name: 'name', type: 'string' },
      { name: 'price', type: 'number' },
      { name: 'category', type: 'string' },
      { name: 'in_stock', type: 'boolean' }
    ],
    rows: [
      [101, 'Laptop', 1299.99, 'Electronics', true],
      [102, 'Smartphone', 899.99, 'Electronics', true],
      [103, 'Headphones', 199.99, 'Electronics', false],
      [104, 'Desk Chair', 249.99, 'Furniture', true],
      [105, 'Coffee Table', 349.99, 'Furniture', true],
      [106, 'Bookshelf', 199.99, 'Furniture', false],
      [107, 'Water Bottle', 24.99, 'Kitchen', true],
      [108, 'Blender', 89.99, 'Kitchen', true]
    ]
  },
  orders: {
    name: 'orders',
    columns: [
      { name: 'id', type: 'number' },
      { name: 'user_id', type: 'number' },
      { name: 'total', type: 'number' },
      { name: 'status', type: 'string' },
      { name: 'order_date', type: 'date' }
    ],
    rows: [
      [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
      [1002, 2, 899.99, 'Shipped', '2023-06-20'],
      [1003, 3, 249.99, 'Processing', '2023-06-25'],
      [1004, 1, 89.99, 'Delivered', '2023-07-01'],
      [1005, 4, 549.98, 'Shipped', '2023-07-05'],
      [1006, 5, 199.99, 'Processing', '2023-07-10']
    ]
  }
};

// Simple SQL parser function
const parseSql = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
  sqlQuery = sqlQuery.trim();
  
  // Basic SELECT statement parsing
  if (sqlQuery.toLowerCase().startsWith('select')) {
    // Very basic parsing - not a production-ready SQL parser
    const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);
    
    if (!selectMatch) {
      throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
    }
    
    const columns = selectMatch[1].trim();
    const tableName = selectMatch[2].trim();
    const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
    const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
    const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;
    
    // Check if table exists
    if (!database[tableName]) {
      throw new Error(`Table '${tableName}' does not exist`);
    }
    
    const table = database[tableName];
    
    // Parse columns
    let selectedColumns: number[] = [];
    let columnNames: string[] = [];
    
    if (columns === '*') {
      // Select all columns
      selectedColumns = table.columns.map((_, index) => index);
      columnNames = table.columns.map(col => col.name);
    } else {
      // Select specific columns
      const colList = columns.split(',').map(col => col.trim());
      
      colList.forEach(colName => {
        const colIndex = table.columns.findIndex(col => col.name === colName);
        if (colIndex === -1) {
          throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
        }
        selectedColumns.push(colIndex);
        columnNames.push(colName);
      });
    }
    
    // Filter rows based on WHERE clause (very basic implementation)
    let filteredRows = [...table.rows];
    
    if (whereClause) {
      // This is a very simplistic WHERE parser that only supports basic equality conditions
      const whereMatch = whereClause.match(/(\w+)\s*(=|!=|>|<|>=|<=)\s*([^;]*)/);
      
      if (whereMatch) {
        const whereColumn = whereMatch[1].trim();
        const whereOperator = whereMatch[2].trim();
        let whereValue = whereMatch[3].trim();
        
        // Remove quotes from string values
        if ((whereValue.startsWith("'") && whereValue.endsWith("'")) || 
            (whereValue.startsWith('"') && whereValue.endsWith('"'))) {
          whereValue = whereValue.substring(1, whereValue.length - 1);
        }
        
        const colIndex = table.columns.findIndex(col => col.name === whereColumn);
        
        if (colIndex === -1) {
          throw new Error(`Column '${whereColumn}' does not exist in table '${tableName}'`);
        }
        
        filteredRows = filteredRows.filter(row => {
          const cellValue = row[colIndex];
          
          switch (whereOperator) {
            case '=':
              return cellValue == whereValue; // Using loose equality for different types
            case '!=':
              return cellValue != whereValue;
            case '>':
              return cellValue > whereValue;
            case '<':
              return cellValue < whereValue;
            case '>=':
              return cellValue >= whereValue;
            case '<=':
              return cellValue <= whereValue;
            default:
              return false;
          }
        });
      } else {
        throw new Error('Invalid WHERE clause format');
      }
    }
    
    // Apply ORDER BY (very basic implementation)
    if (orderByClause) {
      const orderParts = orderByClause.split(' ');
      const orderColumn = orderParts[0].trim();
      const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';
      
      const colIndex = table.columns.findIndex(col => col.name === orderColumn);
      
      if (colIndex === -1) {
        throw new Error(`Column '${orderColumn}' does not exist in table '${tableName}'`);
      }
      
      filteredRows.sort((a, b) => {
        const valueA = a[colIndex];
        const valueB = b[colIndex];
        
        if (valueA < valueB) return isDesc ? 1 : -1;
        if (valueA > valueB) return isDesc ? -1 : 1;
        return 0;
      });
    }
    
    // Apply LIMIT
    if (limitClause !== null && !isNaN(limitClause)) {
      filteredRows = filteredRows.slice(0, limitClause);
    }
    
    // Project selected columns
    const resultRows = filteredRows.map(row => 
      selectedColumns.map(colIndex => row[colIndex])
    );
    
    return {
      columns: columnNames,
      rows: resultRows
    };
  } else {
    throw new Error('Only SELECT queries are supported');
  }
};

// Helper function to convert query results to Excel
const resultsToExcel = (
  results: { columns: string[], rows: any[][] },
  sheetName: string = 'Sheet1'
): XLSX.WorkBook => {
  // Create a worksheet
  const worksheet = XLSX.utils.aoa_to_sheet([results.columns, ...results.rows]);
  
  // Create a workbook and add the worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  
  // Additional formatting options can be applied here
  
  return workbook;
};

// Main component to convert SQL to Excel
const SqlToExcel: React.FC<SqlToExcelProps> = () => {
  // State for the SQL input
  const [sqlInput, setSqlInput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('export');
  // State for the sheet name
  const [sheetName, setSheetName] = useState<string>('Sheet1');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing database schema
  const [showSchema, setShowSchema] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isExcelGenerated, setIsExcelGenerated] = useState<boolean>(false);
  
  // Handle SQL input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setSqlInput(e.target.value);
    setError('');
    setIsExcelGenerated(false);
  };
  
  // Use example query
  const useExampleQuery = () => {
    const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
    setSqlInput(exampleQuery);
    setError('');
    setIsExcelGenerated(false);
  };
  
  // Convert SQL to Excel
  const convertSqlToExcel = () => {
    setError('');
    setIsLoading(true);
    setIsExcelGenerated(false);
    
    if (!sqlInput.trim()) {
      setError('Please enter an SQL query');
      setIsLoading(false);
      return;
    }
    
    try {
      // Parse the SQL query and execute it against our mock database
      const results = parseSql(sqlInput, mockDatabase);
      
      // Generate the Excel workbook
      const workbook = resultsToExcel(results, sheetName);
      
      // Convert to an array buffer
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      
      // Convert buffer to Blob
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = `${fileName || 'export'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setIsExcelGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error executing SQL query: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };
  
  // Clear all inputs and reset states
  const clearAll = () => {
    setSqlInput('');
    setError('');
    setFileName('export');
    setSheetName('Sheet1');
    setIsExcelGenerated(false);
  };
  
  // Render database schema
  const renderDatabaseSchema = () => {
    return (
      <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
        <h3 className="font-medium mb-2">Database Schema</h3>
        {Object.values(mockDatabase).map(table => (
          <div key={table.name} className="mb-3">
            <div className="font-medium">{table.name}</div>
            <div className="pl-4">
              {table.columns.map((col, index) => (
                <div key={index}>
                  {col.name} ({col.type})
                </div>
              ))}
            </div>
          </div>
        ))}
        <p className="mt-3 text-blue-800">
          This is a mock database for demonstration. In a real application, you would 
          connect to an actual database or database API.
        </p>
      </div>
    );
  };
  
  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">SQL to Excel Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Execute SQL queries and convert the results to Excel format. This demo uses a mock in-memory database with sample data.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="sqlInput" className="font-medium text-gray-700">
                SQL Query
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleQuery}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setSqlInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            
            <textarea
              id="sqlInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={sqlInput}
              onChange={handleInputChange}
              placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
            />
            
            <div className="mt-3">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowSchema(!showSchema)}
              >
                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showSchema && (
              <div className="mt-3">
                {renderDatabaseSchema()}
              </div>
            )}
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                Output File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="sheetName" className="block text-sm font-medium text-gray-700 mb-1">
                Sheet Name
              </label>
              <input
                type="text"
                id="sheetName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="Enter sheet name"
              />
            </div>
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertSqlToExcel}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Generate Excel'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Excel Output
            </label>
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isExcelGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    Click "Generate Excel" to create and download an Excel file from your SQL query.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isExcelGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="bg-green-50 p-6 rounded-md text-center flex-grow flex flex-col items-center justify-center border border-green-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Excel File Generated!</h3>
                  <p className="text-green-700 mb-4">
                    Your Excel file has been generated and downloaded.
                  </p>
                  <p className="text-green-700 mb-2">
                    <span className="font-medium">File Name:</span> {fileName}.xlsx
                  </p>
                  <p className="text-green-700">
                    <span className="font-medium">Sheet Name:</span> {sheetName}
                  </p>
                  <button 
                    onClick={convertSqlToExcel}
                    className="mt-6 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Download Again
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About SQL to Excel Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
            <p className="text-gray-600">
              This SQL to Excel converter allows you to execute SQL queries against a mock database and convert the results to Excel format (.xlsx). In a production environment, you would connect this to a real database or API. This demo provides a simulation of what that would look like, complete with sample data.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>SQL Query Execution</strong> - Execute SELECT queries against the mock database</li>
              <li><strong>Excel File Generation</strong> - Convert query results to Excel .xlsx format</li>
              <li><strong>Custom File Naming</strong> - Specify the output file name</li>
              <li><strong>Custom Sheet Naming</strong> - Specify the sheet name in the Excel file</li>
              <li><strong>Database Schema View</strong> - View the available tables and columns</li>
              <li><strong>Automatic Download</strong> - Automatically download the generated Excel file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Uses a mock in-memory database with predefined data</li>
              <li>Only supports basic SELECT queries with simple WHERE clauses</li>
              <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
              <li>No support for parameterized queries</li>
              <li>Basic Excel formatting (no custom styles, formulas, etc.)</li>
              <li>No transaction support</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Excel Features</h3>
            <p className="text-gray-600">
              Excel (.xlsx) format offers several advantages over CSV:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Multiple Sheets</strong> - Excel files can contain multiple sheets</li>
              <li><strong>Cell Formatting</strong> - Support for cell styles, colors, borders, etc.</li>
              <li><strong>Data Types</strong> - Proper handling of numbers, dates, and other data types</li>
              <li><strong>Formulas</strong> - Support for formulas and calculations</li>
              <li><strong>Filtering and Sorting</strong> - Built-in data filtering and sorting capabilities</li>
              <li><strong>Charts and Graphs</strong> - Ability to include visual representations of data</li>
              <li><strong>Unicode Support</strong> - Better handling of international characters</li>
              <li><strong>Direct Excel Opening</strong> - Opens directly in Excel without import steps</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>SELECT columns</strong> - Specify columns to select, or use * for all columns</li>
              <li><strong>FROM table</strong> - Select data from available tables (users, products, orders)</li>
              <li><strong>WHERE condition</strong> - Basic comparison operators</li>
              <li><strong>ORDER BY column</strong> - Sort by a column (ASC or DESC)</li>
              <li><strong>LIMIT number</strong> - Limit the number of results</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Production Implementation Considerations</h3>
            <p className="text-gray-600">
              In a production environment, you would want to:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Connect to a real database using a database driver or ORM</li>
              <li>Implement proper SQL query parameterization to prevent SQL injection</li>
              <li>Add authentication and authorization for database access</li>
              <li>Add more advanced Excel formatting options (styles, conditional formatting, etc.)</li>
              <li>Add support for multiple sheets in a single workbook</li>
              <li>Add formula support for calculated fields</li>
              <li>Implement query timeouts and limits to prevent resource exhaustion</li>
              <li>Use a more robust SQL parser or query builder</li>
              <li>Add error handling and logging for database errors</li>
              <li>Implement pagination or streaming for large result sets</li>
              <li>Add caching for frequently used queries</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Sample Queries for This Demo</h3>
            <div className="bg-gray-50 p-4 rounded-md font-mono text-sm">
              <p className="mb-2">-- Get all users</p>
              <p className="mb-4">SELECT * FROM users;</p>
              
              <p className="mb-2">-- Get products in a specific category</p>
              <p className="mb-4">SELECT id, name, price FROM products WHERE category = "Electronics";</p>
              
              <p className="mb-2">-- Get orders with a specific status</p>
              <p className="mb-4">SELECT * FROM orders WHERE status = "Shipped";</p>
              
              <p className="mb-2">-- Get expensive products, ordered by price</p>
              <p className="mb-4">SELECT * FROM products WHERE price 100 ORDER BY price DESC;</p>
              
              <p className="mb-2">-- Get the top 3 most recent orders</p>
              <p>SELECT * FROM orders ORDER BY order_date DESC LIMIT 3;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlToExcel;